import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { Label } from '../../../components/ui/Label';
import { Select } from '../../../components/ui/Select';
import { useNotification } from '../../../contexts/NotificationContext';

export const CREW_FILE_SECTION_OPTIONS: Array<{
  value: string;
  label: string;
  singleRecord?: boolean;
  visibilityOnly?: boolean;
}> = [
  { value: 'dashboard', label: 'Dashboard', visibilityOnly: true },
  { value: 'personal', label: 'Personal', singleRecord: true },
  { value: 'contact', label: 'Contact', singleRecord: true },
  { value: 'availability', label: 'Availability', singleRecord: true },
  { value: 'certs', label: 'Documents' },
  { value: 'allottees', label: 'Allottees' },
  { value: 'nok', label: 'Next of kin' },
  { value: 'appraisals', label: 'Appraisals' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'prevService', label: 'Previous service' },
  { value: 'transactions', label: 'Company Services' },
  { value: 'medicals', label: 'Medical' },
  { value: 'documents', label: 'Other Files' },
  { value: 'replacements', label: 'Audit Log' },
];

export type SectionAccessRow = {
  section: string;
  visible: boolean;
  canModify: boolean;
  canAdd: boolean;
  canDelete: boolean;
};

type AgencyUser = {
  id: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName?: string | null;
  manningAgentId?: number | null;
  manningAgent?: { id: number; name: string } | null;
};

function defaultRows(): SectionAccessRow[] {
  return CREW_FILE_SECTION_OPTIONS.map((opt) => {
    const openDefaults = new Set([
      'personal',
      'contact',
      'availability',
      'certs',
      'nok',
      'prevService',
      'medicals',
      'documents',
    ]);
    const viewOnly = opt.value === 'transactions';
    const visible = openDefaults.has(opt.value) || viewOnly;
    const canModify = visible && !viewOnly && !opt.visibilityOnly;
    const canAdd = canModify && !opt.singleRecord && !opt.visibilityOnly;
    const canDelete = canAdd;
    return {
      section: opt.value,
      visible,
      canModify,
      canAdd,
      canDelete,
    };
  });
}

function normalizeRow(
  row: SectionAccessRow,
  meta: (typeof CREW_FILE_SECTION_OPTIONS)[number],
): SectionAccessRow {
  if (!row.visible) {
    return { section: row.section, visible: false, canModify: false, canAdd: false, canDelete: false };
  }
  if (meta.visibilityOnly) {
    return { section: row.section, visible: true, canModify: false, canAdd: false, canDelete: false };
  }
  const canModify = Boolean(row.canModify);
  const canAdd = meta.singleRecord ? false : Boolean(row.canAdd);
  const canDelete = meta.singleRecord ? false : Boolean(row.canDelete);
  return { section: row.section, visible: true, canModify, canAdd, canDelete };
}

const ManningAgentPortalPanel: React.FC<{ agentId: number }> = ({ agentId }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [sections, setSections] = useState<SectionAccessRow[]>(defaultRows);
  const [linkedUserIds, setLinkedUserIds] = useState<number[]>([]);
  const [candidates, setCandidates] = useState<AgencyUser[]>([]);
  const [pickUserId, setPickUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingSections, setSavingSections] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [secRes, usersRes, candRes] = await Promise.all([
        api.get(`/crew-lookups/manning-agents/${agentId}/sections`),
        api.get(`/crew-lookups/manning-agents/${agentId}/users`),
        api.get('/crew-lookups/manning-agent-users'),
      ]);
      const incoming = (secRes.data?.sections ?? []) as SectionAccessRow[];
      const bySection = new Map(incoming.map((r) => [r.section, r]));
      setSections(
        CREW_FILE_SECTION_OPTIONS.map((opt) => {
          const row = bySection.get(opt.value) ?? {
            section: opt.value,
            visible: false,
            canModify: false,
            canAdd: false,
            canDelete: false,
          };
          return normalizeRow(row, opt);
        }),
      );
      setLinkedUserIds(((usersRes.data?.users ?? []) as AgencyUser[]).map((u) => u.id));
      setCandidates((candRes.data ?? []) as AgencyUser[]);
    } catch (e: any) {
      showError(e?.response?.data?.message || 'Failed to load manning agent portal settings');
    } finally {
      setLoading(false);
    }
  }, [agentId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedUsers = useMemo(
    () => candidates.filter((u) => linkedUserIds.includes(u.id)),
    [candidates, linkedUserIds],
  );

  const availableCandidates = useMemo(
    () =>
      candidates.filter(
        (u) => !linkedUserIds.includes(u.id) && (u.manningAgentId == null || u.manningAgentId === agentId),
      ),
    [candidates, linkedUserIds, agentId],
  );

  const updateSection = (section: string, patch: Partial<SectionAccessRow>) => {
    setSections((prev) =>
      prev.map((row) => {
        if (row.section !== section) return row;
        const meta = CREW_FILE_SECTION_OPTIONS.find((o) => o.value === section)!;
        return normalizeRow({ ...row, ...patch, section }, meta);
      }),
    );
  };

  const setViewOnly = (section: string) => {
    updateSection(section, { visible: true, canModify: false, canAdd: false, canDelete: false });
  };

  const saveSections = async () => {
    setSavingSections(true);
    try {
      await api.put(`/crew-lookups/manning-agents/${agentId}/sections`, { sections });
      showSuccess('Crew file permissions saved');
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSavingSections(false);
    }
  };

  const saveUsers = async (nextIds: number[]) => {
    setSavingUsers(true);
    try {
      await api.put(`/crew-lookups/manning-agents/${agentId}/users`, { userIds: nextIds });
      setLinkedUserIds(nextIds);
      showSuccess('Linked users saved');
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.message || 'Failed to save users');
    } finally {
      setSavingUsers(false);
    }
  };

  if (loading) {
    return <p className={cn('text-sm', colors.textSecondary)}>Loading portal settings…</p>;
  }

  return (
    <div className="space-y-6">
      <div className={cn('rounded-xl border p-4 space-y-3', colors.border, colors.surface)}>
        <div>
          <h3 className={cn('text-sm font-semibold', colors.text)}>Linked users</h3>
          <p className={cn('text-xs mt-1', colors.textMuted)}>
            Users with Type Manning agent that share this agency. They inherit the tab permissions below.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="min-w-[240px] flex-1">
            <Label htmlFor="agency-user-pick">Add user</Label>
            <Select
              id="agency-user-pick"
              value={pickUserId}
              onChange={(e) => setPickUserId(e.target.value)}
              className="mt-1"
              options={[
                { value: '', label: 'Select user…' },
                ...availableCandidates.map((u) => ({
                  value: String(u.id),
                  label:
                    u.userDisplayName ||
                    `${u.userFirstName} ${u.userLastName}`.trim() ||
                    u.userEmail,
                })),
              ]}
            />
          </div>
          <Button
            type="button"
            disabled={!pickUserId || savingUsers}
            loading={savingUsers}
            onClick={() => {
              const id = Number(pickUserId);
              if (!Number.isFinite(id)) return;
              void saveUsers(Array.from(new Set([...linkedUserIds, id])));
              setPickUserId('');
            }}
          >
            Add
          </Button>
        </div>
        <ul className="space-y-2">
          {linkedUsers.length === 0 && (
            <li className={cn('text-sm', colors.textMuted)}>No users linked yet.</li>
          )}
          {linkedUsers.map((u) => (
            <li
              key={u.id}
              className={cn(
                'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm',
                colors.border,
              )}
            >
              <span className={colors.text}>
                {u.userDisplayName || `${u.userFirstName} ${u.userLastName}`.trim() || u.userEmail}
                <span className={cn('ml-2 text-xs', colors.textMuted)}>{u.userEmail}</span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={savingUsers}
                onClick={() => void saveUsers(linkedUserIds.filter((id) => id !== u.id))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <div className={cn('rounded-xl border p-4 space-y-3', colors.border, colors.surface)}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className={cn('text-sm font-semibold', colors.text)}>Crew file tabs</h3>
            <p className={cn('text-xs mt-1', colors.textMuted)}>
              Choose whether each tab is visible, then View only / Modify / Add / Delete.
            </p>
          </div>
          <Button type="button" loading={savingSections} disabled={savingSections} onClick={() => void saveSections()}>
            Save permissions
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left', colors.border)}>
                <th className={cn('py-2 pr-3 font-medium', colors.textSecondary)}>Tab</th>
                <th className={cn('py-2 px-2 font-medium', colors.textSecondary)}>Visible</th>
                <th className={cn('py-2 px-2 font-medium', colors.textSecondary)}>View only</th>
                <th className={cn('py-2 px-2 font-medium', colors.textSecondary)}>Modify</th>
                <th className={cn('py-2 px-2 font-medium', colors.textSecondary)}>Add</th>
                <th className={cn('py-2 px-2 font-medium', colors.textSecondary)}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((row) => {
                const meta = CREW_FILE_SECTION_OPTIONS.find((o) => o.value === row.section)!;
                const disabledActions = !row.visible || Boolean(meta.visibilityOnly);
                const viewOnly =
                  row.visible && !row.canModify && !row.canAdd && !row.canDelete;
                return (
                  <tr key={row.section} className={cn('border-b', colors.border)}>
                    <td className={cn('py-2 pr-3', colors.text)}>{meta.label}</td>
                    <td className="py-2 px-2">
                      <Switch
                        checked={row.visible}
                        onChange={(e) =>
                          updateSection(row.section, {
                            visible: e.target.checked,
                            canModify: e.target.checked && !meta.visibilityOnly,
                            canAdd: e.target.checked && !meta.singleRecord && !meta.visibilityOnly,
                            canDelete:
                              e.target.checked && !meta.singleRecord && !meta.visibilityOnly,
                          })
                        }
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Switch
                        checked={viewOnly}
                        disabled={!row.visible || Boolean(meta.visibilityOnly)}
                        onChange={(e) => {
                          if (e.target.checked) setViewOnly(row.section);
                          else updateSection(row.section, { canModify: true });
                        }}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Switch
                        checked={row.canModify}
                        disabled={disabledActions}
                        onChange={(e) => updateSection(row.section, { canModify: e.target.checked })}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Switch
                        checked={row.canAdd}
                        disabled={disabledActions || Boolean(meta.singleRecord)}
                        onChange={(e) => updateSection(row.section, { canAdd: e.target.checked })}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Switch
                        checked={row.canDelete}
                        disabled={disabledActions || Boolean(meta.singleRecord)}
                        onChange={(e) => updateSection(row.section, { canDelete: e.target.checked })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManningAgentPortalPanel;
